/**
 * @fileoverview Spinner loading component for button actions.
 *
 * Provides a circular spinner component for use in buttons and other
 * interactive elements during async operations.
 */

/**
 * Size variants for the spinner.
 */
type SpinnerSize = 'sm' | 'md' | 'lg' | 'xl';

/**
 * Color variants for the spinner.
 */
type SpinnerColor = 'white' | 'blue' | 'slate';

/**
 * Props for the Spinner component.
 */
export interface SpinnerProps {
  /** Size variant - sm: 16px, md: 20px, lg: 24px, xl: 32px */
  size?: SpinnerSize;
  /** Color variant - affects border colors */
  color?: SpinnerColor;
  /** Optional label for screen readers */
  label?: string;
  /** Additional CSS classes */
  className?: string;
}

/**
 * Size class mappings.
 */
const SIZE_CLASSES: Record<SpinnerSize, string> = {
  sm: 'h-4 w-4 border-2',
  md: 'h-5 w-5 border-2',
  lg: 'h-6 w-6 border-2',
  xl: 'h-8 w-8 border-4',
};

/**
 * Color class mappings for the spinner track (background) and indicator (spinning part).
 */
const COLOR_CLASSES: Record<SpinnerColor, { track: string; indicator: string }> = {
  white: {
    track: 'border-white/30',
    indicator: 'border-t-white',
  },
  blue: {
    track: 'border-blue-500/30',
    indicator: 'border-t-blue-500',
  },
  slate: {
    track: 'border-slate-500/30',
    indicator: 'border-t-slate-400',
  },
};

/**
 * Circular spinner component for indicating loading state.
 *
 * Use this in buttons and interactive elements during async operations
 * to provide feedback that an action is in progress.
 *
 * @param props - Component props
 * @param props.size
 * @param props.color
 * @param props.label
 * @param props.className
 * @returns Animated spinner element
 *
 * @example
 * ```tsx
 * // In a button
 * <button disabled={isLoading}>
 *   {isLoading ? <Spinner size="sm" color="white" /> : 'Submit'}
 * </button>
 *
 * // Standalone with label
 * <Spinner size="lg" color="blue" label="Loading data..." />
 * ```
 */
export function Spinner({
  size = 'md',
  color = 'white',
  label,
  className = '',
}: SpinnerProps): JSX.Element {
  const sizeClass = SIZE_CLASSES[size];
  const colorClass = COLOR_CLASSES[color];

  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      role="status"
      aria-live="polite"
    >
      <div
        className={`animate-spin rounded-full ${sizeClass} ${colorClass.track} ${colorClass.indicator}`}
        aria-hidden="true"
      />
      {label ? (
        <span className="text-sm text-slate-400">{label}</span>
      ) : (
        <span className="sr-only">Loading...</span>
      )}
    </div>
  );
}

/**
 * Spinner with accompanying text for inline use.
 * Useful for showing status during operations.
 *
 * @param props - Component props
 * @param props.text - Text to display next to the spinner
 * @param props.size - Spinner size (default: sm)
 * @param props.className - Additional CSS classes
 * @returns Spinner with text element
 *
 * @example
 * ```tsx
 * <SpinnerWithText text="Saving changes..." />
 * ```
 */
export function SpinnerWithText({
  text,
  size = 'sm',
  className = '',
}: {
  text: string;
  size?: SpinnerSize;
  className?: string;
}): JSX.Element {
  return (
    <div
      className={`inline-flex items-center gap-2 ${className}`}
      role="status"
      aria-live="polite"
    >
      <Spinner size={size} color="blue" />
      <span className="text-sm text-slate-400">{text}</span>
    </div>
  );
}
